import { useMemo } from "react";

import { useAuth } from "@/app/context/Auth/AuthContext";

import { useAllAnswers, useAssessments } from "./api";
import { missQuestions, requiredStats } from "./stats";

export interface FitCheckImpact {
	rolesChecked: number;
	/** Required must-haves the CV missed, across every check — the questions the user was asked. */
	flaggedGaps: number;
	/** Answered "have": a gap on the CV that the user actually has. A quick résumé fix. */
	fixable: number;
	/** Answered "lack": a genuine gap. */
	realGaps: number;
	answered: number;
	unanswered: number;
	/** Required must-haves already shown on the CV, summed across checks. */
	mustHavesMet: number;
	mustHavesTotal: number;
	/** Met once the user confirms the "have" gaps — met + fixable, capped at the total. */
	mustHavesAfter: number;
	isLoading: boolean;
	hasData: boolean;
	hasAnswers: boolean;
}

/**
 * The value story across a user's FitChecks: how many flagged gaps were merely off the résumé versus
 * real, and how much confirming them lifts the on-paper match. Reads the two queries the dashboard
 * already holds, so it adds no requests.
 */
export function useFitCheckImpact(): FitCheckImpact {
	const { userData } = useAuth();
	const userId = userData?.userID || "";
	const { data: records, isLoading: recordsLoading } = useAssessments(userId);
	const { data: answersByJob, isLoading: answersLoading } = useAllAnswers(userId);

	return useMemo(() => {
		const list = records ?? [];
		let flaggedGaps = 0;
		let fixable = 0;
		let realGaps = 0;
		let mustHavesMet = 0;
		let mustHavesTotal = 0;

		for (const record of list) {
			const { met, total } = requiredStats(record.assessment);
			mustHavesMet += met;
			mustHavesTotal += total;
			flaggedGaps += missQuestions(record.assessment).length;

			const answers = answersByJob?.[record.jobId] ?? [];
			fixable += answers.filter((answer) => answer.answer === "have").length;
			realGaps += answers.filter((answer) => answer.answer === "lack").length;
		}

		const answered = fixable + realGaps;
		return {
			rolesChecked: list.length,
			flaggedGaps,
			fixable,
			realGaps,
			answered,
			unanswered: Math.max(0, flaggedGaps - answered),
			mustHavesMet,
			mustHavesTotal,
			mustHavesAfter: Math.min(mustHavesTotal, mustHavesMet + fixable),
			isLoading: recordsLoading || answersLoading,
			hasData: list.length > 0,
			hasAnswers: answered > 0,
		};
	}, [records, answersByJob, recordsLoading, answersLoading]);
}
